import React from 'react';
import {
  Box,
  Typography,
  Link,
  Container,
  LinearProgress,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useAppTheme } from '../theme';
import type { ThemedSxStyle } from '../types/themedStyles';
import transcriptionJobsStore from '../stores/TranscriptionJobsStore';
import { translate } from '../i18n/translate';
import { zIndex } from '../theme/styles/utilities';
import { spacing } from '../theme/spacing';

/** Footer vertical spacing is 25% shorter than the original baseline. */
const FOOTER_HEIGHT_SCALE = 0.75;

const footerVerticalPaddingPx = spacing.lg * FOOTER_HEIGHT_SCALE;
const footerContentHeightRem = 4.5 * FOOTER_HEIGHT_SCALE;
const footerMainClearancePx =
  (spacing.xxxl + spacing.xxl + spacing.xl + spacing.lg + spacing.md) * FOOTER_HEIGHT_SCALE;

/** Desktop footer height (padding + content + safe area). */
export const APP_FOOTER_DESKTOP_OFFSET = `calc(${footerVerticalPaddingPx * 2}px + ${footerContentHeightRem}rem + env(safe-area-inset-bottom, 0px))`;

/**
 * Viewport `bottom` offset for fixed UI (e.g. map icon tray) that should sit above the app footer.
 * On mobile the footer is hidden, so App sets `--app-footer-offset` to the safe-area inset only.
 */
export const APP_FOOTER_BOTTOM_OFFSET = `var(--app-footer-offset, ${APP_FOOTER_DESKTOP_OFFSET})`;

/**
 * Bottom padding for the main app column so scrollable content stays above the fixed footer
 * (copyright links plus optional transcription progress).
 */
export const appMainFooterClearanceSx: ThemedSxStyle = () => ({
  flex: 1,
  pb: `calc(${footerMainClearancePx}px + env(safe-area-inset-bottom, 0px))`,
});

interface FooterProps {
  /** Force a white bar (used on celebrate / memorial landing pages). */
  light?: boolean;
}

const Footer: React.FC<FooterProps> = observer(({ light = false }) => {
  const { themed } = useAppTheme();
  const active = transcriptionJobsStore.activeCount;

  return (
    <Box
      component="footer"
      sx={themed($footer(light))}
    >
      {active > 0 ? (
        <Box sx={themed($transcriptionProgressWrap)}>
          <LinearProgress color="primary" sx={themed($linearProgress)} />
          <Typography variant="caption" component="p" sx={themed($transcriptionProgressLabel)}>
            {active === 1
              ? translate('common.transcriptionInProgress')
              : translate('common.transcriptionsInProgress', { count: String(active) })}
          </Typography>
        </Box>
      ) : null}
      <Container maxWidth="sm">
        <Typography 
          variant="body2" 
          align="center"
          sx={themed($copyrightText(light))}
        >
          © {new Date().getFullYear()} Overhear ltd. All rights reserved.
        </Typography>
        <Typography 
          variant="body2" 
          align="center" 
          sx={themed($linksContainer(light))}
        >
          <Link
            component={RouterLink}
            to="/privacy"
            underline="hover"
            sx={themed($link(light))}
          >
            Privacy Policy
          </Link>
          <Link
            href="mailto:hello@theoverhear.app"
            underline="hover"
            sx={themed($link(light))}
          >
            Contact Us
          </Link>
        </Typography>
      </Container>
    </Box>
  );
});

const $footer = (light: boolean): ThemedSxStyle => ({ colors }) => ({
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: zIndex.adminNav - 1,
  px: 2,
  pt: `${footerVerticalPaddingPx}px`,
  pb: `calc(${footerVerticalPaddingPx}px + env(safe-area-inset-bottom, 0px))`,
  backgroundColor: light ? '#ffffff' : colors.palette.input.background,
  borderTop: `1px solid ${light ? '#e8e8e8' : colors.palette.border}`,
  transition: 'background-color 0.3s ease, border-color 0.3s ease',
});

const $transcriptionProgressWrap: ThemedSxStyle = ({ spacing }) => ({
  width: '100%',
  maxWidth: 560,
  mx: 'auto',
  mb: `${spacing.md * FOOTER_HEIGHT_SCALE}px`,
});

const $linearProgress: ThemedSxStyle = () => ({
  height: 4,
  borderRadius: 2,
});

const $transcriptionProgressLabel: ThemedSxStyle = ({ spacing, colors }) => ({
  mt: `${spacing.xs}px`,
  mb: 0,
  textAlign: 'center',
  color: colors.textDim,
});

const $mutedFooterText = (light: boolean, colors: { background: string }): string =>
  light || colors.background !== '#121212' ? '#666666' : '#888888';

const $copyrightText = (light: boolean): ThemedSxStyle => ({ colors }) => ({
  color: $mutedFooterText(light, colors),
  transition: 'color 0.3s ease'
});

const $linksContainer = (light: boolean): ThemedSxStyle => ({ colors }) => ({
  mt: FOOTER_HEIGHT_SCALE,
  color: $mutedFooterText(light, colors),
  transition: 'color 0.3s ease'
});

const $link = (light: boolean): ThemedSxStyle => ({ colors }) => ({
  mr: 2,
  color: $mutedFooterText(light, colors),
  '&:hover': {
    color: colors.palette.main
  },
  transition: 'color 0.3s ease'
});

export default Footer;
